import {Component, EventEmitter, Input, OnInit, OnDestroy, Output, ViewEncapsulation, HostListener, AfterViewInit} from '@angular/core';
import { Subscription } from 'rxjs';

import {FiltersService} from 'projects/common/services/filters.service';
import {UtilsService} from 'projects/common/services/utils.service';
import { DiscreteFilter, Filter, FilterActionsState, FilterValue, RangeFilter, OffersFilterData, InputFilter } from './offers-filters-model';
import {SliderRange} from 'projects/common/components/slider-range/slider-range.component';
import { LandingModelService } from 'projects/common/services/landingModel.service';
import { ScreenType } from '@shopping-angular-components/common-library';
import { ScrollService } from 'projects/common/services/scroll.service';

@Component({
  selector: 'offers-filters-content',
  template: `
    <div class="offers-filters-content">
      <ng-container *ngFor="let filter of getFilterValues(); let last = last">
        <div [ngClass]="{'border-right': !last, 'filter-column': showAll}" *ngIf="!filter.hidden || filtersService.isForcedVisible(filter.name)">
          <div *ngIf="!isMobile && showAll" class="eva-3-h4 -eva-3-mb-xlg">{{filter.name}}</div>

          <ng-container *ngIf="filter.type === 'discrete'">
            <discrete-filter [filter]="filter" [showAll]="showAll" [isModal]="isModal" (discreteFilterChange)="updateDiscreteFilter($event)"></discrete-filter>
          </ng-container>

          <ng-container *ngIf="filter.type === 'range'">

            <div *ngIf="!filter.multiselector; else groupedSliders" class="grid-container grid-template range-filter-1" [ngClass]="{'desktop-modal': isModal && !isMobile}">
              <slider-range [sliderRange]="filter.value" sliderType="RANGE" [value_type]="filter.value_type" [currency]="currency" [preselectedRange]="filter.selected!"
                (rangeAppliedEvent)="updateRangeFilter($event, filter)">
              </slider-range>
            </div>

            <ng-template #groupedSliders>
              <div class="grid-container grid-template range-filter-{{filter.values?.length}}"  [ngClass]="{'desktop-modal': isModal && !isMobile}">
                <slider-range
                  *ngFor="let slider of filter.values; let i = index"
                  [sliderRange]="slider" sliderType="RANGE" [value_type]="filter.value_type" [currency]="currency" [preselectedRange]="filter.selected!"
                  (rangeAppliedEvent)="updateRangeFilter($event, filter)">
                </slider-range>
              </div>
            </ng-template>

          </ng-container>

          <ng-container *ngIf="filter.type === 'input'">
           {{'common.to' | translate}}
            <div class="eva-3-input -lg -input-tag">
              <input class="input-tag" [type]="'text'" [placeholder]="filter.placeholder" [ngModel]="filter.value" (ngModelChange)="updateInputFilter($event, filter)" onkeypress="return event.charCode >= 48 && event.charCode <= 57">
            </div>
          </ng-container>

        </div>
      </ng-container>
    </div>
    <div class="filters-buttons" [ngClass]="showAll ? '-eva-3-p-md -border-top' : '-eva-3-pt-md'" *ngIf="!hideActionButtons">
      <eva-button [style.display]="showRemove ? 'block' : 'none'" [options]="{ghost_button: false, type: '-white', size: '-md', link_text: ('common.delete' | translate)}"
        (clickEvent)="removeFilters()"
        [moduleId]="moduleId"
        [moduleIndex]="moduleIndex"
        [uniqueId]="uniqueId">
      </eva-button>
      <eva-button [options]="{ghost_button: false, type: showApplyEnabled ? '-primary' : '-disable', size: '-md', link_text: ('common.apply' | translate)}"
        (clickEvent)="applyFilters()"
        [moduleId]="moduleId"
        [moduleIndex]="moduleIndex"
        [uniqueId]="uniqueId">
      </eva-button>
    </div>
  `,
  styleUrls: ['offers-filters-content.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OffersFiltersContentComponent implements OnInit {
  @Input() filters: { [key: string]: Filter };
  @Input() showAll: boolean;
  @Input() isModal: boolean;
  @Input() hideActionButtons = false;
  @Input() criteria: string;
  @Input() currency: any;
  @Input() moduleIndex: number;
  @Input() moduleId: string;
  @Input() uniqueId?: string;

  @Output() filterChangeEvent = new EventEmitter<FilterActionsState>();
  @Output() applyFiltersEvent = new EventEmitter<void>();
  @Output() removeFiltersEvent = new EventEmitter<void>();

  showRemove = false;
  showApplyEnabled = false;
  firstFiltering = true;
  isMobile: boolean;
  filtersBackup: { [key: string]: Filter };
  tmpFilters: string[] = [];
  selectedFilters: string[] = [];
  
  private filterUnappliedSubscription: Subscription;

  constructor(public filtersService: FiltersService, public landingModelService: LandingModelService) {
    this.isMobile =  this.landingModelService.getLandingModel().device.type === ScreenType.MOBILE;
  }

  ngOnInit() {
    if(!this.showAll) {
      this.filters = { [this.criteria]: this.filters[this.criteria] };
    }

    if (!this.filtersBackup) {
      this.filtersBackup = UtilsService.copy(this.filters);
    }

    this.tmpFilters = this.getSelectedFilters();
    this.showRemove = this.tmpFilters.length > 0;
    
    this.filterUnappliedSubscription = this.filtersService.filterUnappliedEvent.subscribe((event) => {
      this.handleFilterUnapplied(event);
    });
  }
  
  private handleFilterUnapplied(event: { criteria?: string, all?: boolean }): void {
    if (event.criteria && this.filters[event.criteria]) {
      this.tmpFilters = this.getSelectedFilters();
      this.showRemove = this.tmpFilters.length > 0;
      this.showApplyEnabled = false;
      
      this.filterChangeEvent.emit({
        showRemove: this.showRemove,
        showApplyEnabled: this.showApplyEnabled
      });
    } else if (event.all) {
      this.tmpFilters = [];
      this.showRemove = false;
      this.showApplyEnabled = false;
      
      this.filterChangeEvent.emit({
        showRemove: this.showRemove,
        showApplyEnabled: this.showApplyEnabled
      });
    }
  }

  getFilterValues(): Filter[] {
    return Object.values(this.filters);
  }

  private getSelectedFilters(): string[] {
    return Object.values(this.filters)
      .map(filter => {
        if (filter.type === 'discrete') {
          return filter.values?.filter(value => value.selected).map(value => value.id) || [];
        } else if (filter.type === 'range') {
          return this.getRangesValues(filter.value_type, filter.selected);
        } else {
          return filter.value ? [filter.value] : [];
        }
      })
      .reduce((acc, val) => acc.concat(val), []);
  }

  removeFilters() {
    if (this.showAll || this.isModal) {
      this.filtersService.emptyAllFilters();
    } else {
      this.filtersService.emptyAllFilters(this.filters);
    }
    this.showApplyEnabled = this.thereAreChanges();
    this.tmpFilters = [];
    this.showRemove = false;

    this.filterChangeEvent.emit({
      showRemove: this.showRemove,
      showApplyEnabled: this.showApplyEnabled
    });
    this.removeFiltersEvent.emit()
  }

  updateRangeFilter(values: Record<string, SliderRange>, filter: Filter) {
    const rangeFilter = filter as RangeFilter;
    const isNotFilterable = Object.entries(rangeFilter.value).some(([currencyCode, {min, max}]) =>
      min === values[currencyCode]?.min && max === values[currencyCode]?.max);

    if (isNotFilterable) {
      delete rangeFilter.selected;
      this.showRemove = false;
    } else {
      if (rangeFilter.type === 'range' && rangeFilter.multiselector) {
        rangeFilter.selected = {...rangeFilter.selected, ...values};
      } else {
        rangeFilter.selected = values;
      }
      this.showRemove = true;
    }

    this.showApplyEnabled = this.thereAreChanges();
    this.filterChangeEvent.emit({
      showRemove: this.showRemove,
      showApplyEnabled: this.showApplyEnabled
    });
  }

  updateDiscreteFilter(showRemove: boolean) {
    this.showApplyEnabled = this.thereAreChanges();
    this.showRemove = true;

    this.filterChangeEvent.emit({
      showRemove: showRemove,
      showApplyEnabled: this.showApplyEnabled
    });
  }

  updateInputFilter(value, filter: InputFilter) {
    filter.value = value;

    this.showRemove = true;
    this.showApplyEnabled = this.thereAreChanges();

    this.filterChangeEvent.emit({
      showRemove: this.showRemove,
      showApplyEnabled: this.showApplyEnabled
    });
  }

  private thereAreChanges(): boolean {
    this.selectedFilters = this.getSelectedFilters();

    return this.selectedFilters.some(value => !this.tmpFilters.includes(value))
      || this.tmpFilters.some(value => !this.selectedFilters.includes(value));
  }

  applyFilters() {
    if(!this.showApplyEnabled) return;

    this.filtersService.updateHasSelection(this.filters);
    this.filtersService.updateFilterParams(this.moduleIndex, this.uniqueId, true);

    this.showApplyEnabled = false;

    if (!this.firstFiltering) {
      this.filtersBackup = UtilsService.copy(this.filters);
    }

    this.filtersService.applyFilter().then(
      () => {
        this.firstFiltering = false;
        this.tmpFilters = this.selectedFilters;
        this.filtersBackup = UtilsService.copy(this.filters);
        this.applyFiltersEvent.emit();
      },
      () => {
        // Hago esto para seguir soportando el doble binding del modelo
        Object.keys(this.filters).forEach(key => {
          const filter = this.filters[key];
          filter.hasSelection = this.filtersBackup[key].hasSelection;
          if (filter.type === 'discrete') {
              (filter as DiscreteFilter).values = (this.filtersBackup[key] as DiscreteFilter).values;
          }
          filter.selected = this.filtersBackup[key].selected;
        });
      });
  }

  getRangesValues(value_type: string, rangeValue?: Record<string, SliderRange>): string[] {
    if (!rangeValue) {
      return [];
    }

    return Object.entries(rangeValue).map(([currency, {min, minTime, max, maxTime}]) => {
      if (value_type === 'TIME') {
        return `${currency}_${minTime}-${maxTime}`;
      } else {
        return `${currency}_${min}-${max}`;
      }
    });
  }
}


@Component({
  selector: 'offers-filters-modal-content',
  template: `
    <div class="offers-filters-modal-content offers-filters-content">

      <div class="pills-container" *ngIf="isMobile">
        <ng-container *ngFor="let filterEntry of filters | keyvalue : filtersService.filterOrder; let filterIndex = index">
          <eva-tag  *ngIf="!!selectedFilterLabels[filterEntry.key]"
            [active]="true"
            [text]="selectedFilterLabels[filterEntry.key].label"
            [icon]="filterEntry.key === 'flexible_cancellation_policy' ? 'plus' : ''"
            (removeEvent)="removeFilterSelection(filterEntry)">
          </eva-tag>
        </ng-container>
      </div>

      <eva-dropdown>
        <ng-container *ngFor="let filterEntry of filters | keyvalue : filtersService.filterOrder; let i = index">
          <eva-dropdown-item
            *ngIf="filterEntry.key !== 'flexible_cancellation_policy' && (!filterEntry.value.hidden || filtersService.isForcedVisible(filterEntry.value.name)) else flexibleCancelationPolicy"
            [expand]="expandedFilters[filterEntry.value.name]">

            <label label class="dropdown-item-label" [ngClass]="{'inline': !isMobile}" id="filter-{{filterEntry.value.name}}">
              <span class="item-label -lg {{isMobile ? 'eva-3-h5' : 'eva-3-h4'}}">{{filterEntry.value.name}}</span>

              <span *ngIf="filterEntry.value.description && (isMobile || !filterEntry.value.highlight)" [ngClass]="{'-highlight': filterEntry.value.highlight}" class="eva-3-body-2 label-description">
                  {{filterEntry.value.description}}
              </span>
              <span *ngIf="filterEntry.value.description && (!isMobile && filterEntry.value.highlight)" class="eva-3-driver -benefits">
                <span class="driver-text">{{filterEntry.value.description}}</span>
              </span>

            </label>
            <offers-filters-content
              content
              [hideActionButtons]="true"
              [filters]="filters"
              [criteria]="filterEntry.key"
              [currency]="currency"
              [moduleIndex]="moduleIndex"
              [moduleId]="moduleId"
              [uniqueId]="uniqueId"
              [isModal]="isModal"
              (filterChangeEvent)="filterSelectionChangeHandler(filterEntry.key, $event)"
              (applyFiltersEvent)="updateSelectedFilterLabels()"></offers-filters-content>
          </eva-dropdown-item>

          <ng-template #flexibleCancelationPolicy>
            <div *ngIf="(!filterEntry.value.hidden || filtersService.isForcedVisible(filterEntry.value.name)) && asDiscreteFilter(filterEntry.value) as filter" class="dropdown-item">
              <div class="dropdown-item-container">
                <label class="item-label eva-3-h5 -lg" style="width: 100%">
                  <eva-checkbox size="-lg" [checked]="filter.values[0].selected"
                                (clickEvent)="toggleFlexibleCancelationPolicy(filter.values[0])">
                    <span class="filter-label">
                      <span class="filter-name">{{filterEntry.value.name}}</span>
                      <em *ngIf="filter.values[0].amount" class="filter-amount">{{filter.values[0].amount}}</em>
                    </span>
                  </eva-checkbox>
                </label>
              </div>
            </div>
          </ng-template>

          <span *ngIf="filtersWithViewMoreLink[filterEntry.key]" (click)="showMoreGroupHiddenFilters(filterEntry.key, filterEntry.value.group_id)">
            <eva-button [options]="{type: '-link', link_text: filterEntry.value.view_more_link_text, link: ''}"
              [moduleId]="moduleId" [moduleIndex]="moduleIndex" [uniqueId]="uniqueId"
              [nofollow]="true"></eva-button>
          </span>
        </ng-container>
      </eva-dropdown>
    </div>
  `,
  styleUrls: ['./offers-filters-content.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OffersFiltersModalContentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() dataFilters: OffersFilterData;
  @Input() criteria: string;
  @Input() currency: any;
  @Input() moduleIndex: number;
  @Input() moduleId: string;
  @Input() uniqueId?: string;
  @Input() clickedFilterName?: String;
  @Input() clickedGroupId?: String;
  @Input() isMobile?: boolean;
  @Input() isModal = false;

  criteriaHasChanges: { [key: string]: boolean } = {};
  showRemove = false;
  showApplyEnabled = false;

  filters: { [key: string]: Filter };
  selectedFilterLabels: {[key: string]: {label: string, count: number}} = {};
  selectedFiltersQuantity: number = 0;
  filtersWithViewMoreLink: { [key: string]: boolean } = {};
  expandedFilters: { [key: string]: boolean } = {};

  private subscriptions: any[] = [];

  constructor(public filtersService: FiltersService) {
  }

  ngOnInit(): void {
    this.filters = UtilsService.copy(this.dataFilters.filters);
    this.filtersService.updateModalFilterSelections(this.filters);

    this.updateSelectedFilterLabels();
    this.selectedFiltersQuantity = this.filtersService.countFiltersApplied();
    this.subscriptions.push(this.filtersService.filtersAppliedCountEvent.subscribe(({count, filters}) => {
      this.selectedFiltersQuantity = count;
    }));
    this.setViewMoreLinks();

    Object.values(this.filters).forEach((filter, index) => {
      this.expandedFilters[filter.name] = this.hasToExpand(filter, index);
    });
  }

  ngAfterViewInit() {
    if (this.clickedFilterName && this.clickedFilterName !== 'ALL_FILTERS'){
      ScrollService.scrollTo(`filter-${this.clickedFilterName}`);
    }
    if (this.clickedGroupId){
      const groupActiveFilter = Object.entries(this.filters).find(([key, filter]) => filter.group_id === this.clickedGroupId && filter.active);
      const groupFocusedFilter = groupActiveFilter || Object.entries(this.filters).find(([key, filter]) => filter.group_id === this.clickedGroupId);
      if (groupFocusedFilter) {
        ScrollService.scrollTo(`filter-${groupFocusedFilter[1].name}`);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }

  hasToExpand(filter: Filter, index: number): boolean {

    if (this.isMobile) {
      return filter.active || filter.hasSelection;
    }

    const isClickedGroup = !!this.clickedGroupId && filter.group_id === this.clickedGroupId;
    const groupFilters = Object.values(this.filters).filter(f => f.group_id === filter.group_id);
    const groupSelections = groupFilters.filter(f => f.active).length;
    const isExpandedByGroupPill = groupSelections > 1 ? isClickedGroup && filter.hasSelection : isClickedGroup;
    const isFirstOfGroup = groupFilters.indexOf(filter) === 0;
    const isExpandedByAllFiltersPill = this.clickedFilterName === 'ALL_FILTERS' && (!filter.group_id || filter.active || (!groupSelections && isFirstOfGroup));

    return (this.clickedFilterName === filter.name) || isExpandedByAllFiltersPill || isExpandedByGroupPill;
  }

  filterSelectionChangeHandler(criteria: string, filterActionsState: FilterActionsState) {
    this.criteriaHasChanges[criteria] = filterActionsState.showApplyEnabled;
    this.showApplyEnabled = Object.values(this.criteriaHasChanges).some(value => value);
    this.updateSelectedFilterLabels();
    this.filtersService.updateModalFilterSelections(this.filters, this.showApplyEnabled, Object.keys(this.selectedFilterLabels).length);
  }

  updateSelectedFilterLabels(): void {
    this.selectedFilterLabels = this.filtersService.getSelectedFilterLabels(this.filters, false);
  }

  toggleFlexibleCancelationPolicy(value: FilterValue) {
    value.selected = !value.selected;
    this.criteriaHasChanges.flexible_cancellation_policy = !this.criteriaHasChanges.flexible_cancellation_policy;
    this.showApplyEnabled = Object.values(this.criteriaHasChanges).some(val => val);
  }

  asDiscreteFilter(filter: Filter): DiscreteFilter {
    return filter as DiscreteFilter;
  }

  removeFilterSelection(filterEntry: { key: string, value: Filter }, updateLabels = true) {
    this.criteriaHasChanges[filterEntry.key] = !this.criteriaHasChanges[filterEntry.key];
    this.filtersService.emptySingleFilter(filterEntry.value);
    // si se quita filtro como el de aerolíneas o alianzas (preseleccionado por url) que vino oculto, luego debemos visibilizar los filtros aerolíneas y alianzas
    if (filterEntry.value.hidden && !filterEntry.value.group_id) { 
      Object.values(this.filters).filter(f => !f.group_id).forEach(f => f.hidden = false);
      this.filtersService.forceVisible[filterEntry.value.name] = true;
    }
    this.showApplyEnabled = Object.values(this.criteriaHasChanges).some(value => value);
    if (updateLabels) {
      this.updateSelectedFilterLabels();
    }
    this.filtersService.updateModalFilterSelections(this.filters, this.showApplyEnabled, Object.keys(this.selectedFilterLabels).length);
  }

  setViewMoreLinks(){
    const groupsWithLink = {};
    Object.entries(this.filters).forEach(([key, filter]) => {
      if (filter.group_id && (filter.hidden && !this.filtersService.forceVisible[filter.name]) && !groupsWithLink[filter.group_id]){
        this.filtersWithViewMoreLink[key] = true;
        groupsWithLink[filter.group_id] = true;
      }
    });

  }

  showMoreGroupHiddenFilters(oldLinkFilterKey: string, group_id: string | undefined){
    this.filtersWithViewMoreLink[oldLinkFilterKey] = false;
    let groupHiddenFilterIndex = 1;
    const groupHiddenFilters = Object.entries(this.filters).filter(([key, filter]) => filter.group_id === group_id && filter.hidden);
    groupHiddenFilters.forEach(([key, filter]) => {
      if (groupHiddenFilterIndex < 5) {
        filter.hidden = false;
      } else if (groupHiddenFilterIndex === 5 && groupHiddenFilterIndex <= groupHiddenFilters.length) {
        this.filtersWithViewMoreLink[key] = true;
      }
      groupHiddenFilterIndex++;
    })
  }
}
